import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MapIcon from '@mui/icons-material/Map';
import PlaceIcon from '@mui/icons-material/Place';
import {
    Box,
    Collapse,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import React, { ChangeEvent } from 'react';


import FileUpload from '../../components/FileUpload/FileUpload';
import FileView from '../../components/FileUpload/FileView';
import QRCode from '../../components/Print/QRCode';
import { ZONE_OPTIONS } from '../../Utils/utils';
import NoBorderTextField from '../Common/NoBorderTextField';
import TrailerExpandedItems from './TrailerExpandedItems';

type Option = {
    label: string;
    value: string;
};

type Column = {
    accessorKey: string;
    header: string;
    width: number;
};

type TrailerTableProps = {
    trailers: any[];
    columns: Column[];
    expandedRows: string[];
    companies: string[];
    storeOptions: Option[];
    onColumnResize: (accessorKey: string, width: number) => void;
    setTrailers: React.Dispatch<React.SetStateAction<any[]>>;
    onRowClick: (trailer: any) => void;
    onTextChange: (e: React.ChangeEvent<HTMLInputElement>, id: string) => void;
    onDateChange: (e: React.ChangeEvent<HTMLInputElement>, id: string) => void;
    onDataChange: (column: string, value: string, id: string) => void;
    onInfoChange: (column: string, value: string, id: string) => void;
    onMapOpen: (trailer: any) => void;
    onGetMap: (info: any) => void;
    onAddRow: (id: string) => void;
    onDeleteRow: (id: string, index: number) => void;
    onDeleteTrailer: (trailerNo: string) => void;
    onProductChange: (
        value: string,
        id: string,
        index: number,
        column: string
    ) => void;
};

const TrailerTable: React.FC<TrailerTableProps> = ({
    trailers,
    columns,
    expandedRows,
    companies,
    storeOptions,
    onColumnResize,
    setTrailers,
    onRowClick,
    onTextChange,
    onDateChange,
    onDataChange,
    onInfoChange,
    onMapOpen,
    onGetMap,
    onAddRow,
    onDeleteRow,
    onDeleteTrailer,
    onProductChange,
}) => {
    const handleMouseDown = (
        e: React.MouseEvent,
        accessorKey: string,
        initialWidth: number
    ) => {
        e.preventDefault();

        const startX = e.clientX;
        const startWidth = initialWidth;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = startWidth + (moveEvent.clientX - startX);
            onColumnResize(accessorKey, newWidth);
        };

        const handleMouseUp = () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };
    return (
        <Paper variant="outlined" >
            <Table stickyHeader size="small" >
                <TableHead>
                    <TableRow sx={{ 
                        zIndex: 2,
                        position: 'sticky',
                        top: 0,
                        // '& .MuiTableCell-root': { fontSize: 15, py: 1 }
                    }}>
                        <TableCell width={50} />
                        {columns.map((column) => (
                            <TableCell
                                key={column.accessorKey}
                                // sx={{
                                //     width: column.width,
                                //     minWidth: column.width,
                                //     maxWidth: column.width,
                                //     position: 'relative',
                                //     whiteSpace: 'nowrap',
                                // }}
                            >
                                <Box sx={{ pr: 1 }}>{column.header}</Box>

                                <Box
                                    onMouseDown={(e) => handleMouseDown(e, column.accessorKey, column.width)}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: 6,
                                        height: '100%',
                                        cursor: 'col-resize',
                                        zIndex: 10,
                                        '&:hover': {
                                            backgroundColor: 'primary.main',
                                            opacity: 0.2,
                                        },
                                    }}
                                />
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {trailers.map((trailer: any) => {
                        const isExpanded = expandedRows.includes(trailer.id);
                        return (
                            <React.Fragment key={trailer.id}>
                                <TableRow hover>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => onRowClick(trailer)}>
                                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    </TableCell>

                                    {columns.map((column) => {
                                        if (column.accessorKey === 'rental_company') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    <NoBorderTextField
                                                        select
                                                        size="small"
                                                        value={trailer.rental_company || ''}
                                                        onChange={(e) =>
                                                            onDataChange(column.accessorKey, e.target.value, trailer.id)
                                                        }
                                                        fullWidth
                                                    >
                                                        {companies.map((company) => (
                                                            <MenuItem key={company} value={company}>
                                                                {company}
                                                            </MenuItem>
                                                        ))}
                                                    </NoBorderTextField>
                                                </TableCell>
                                            );
                                        }

                                        if (column.accessorKey === 'dclocation') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    <NoBorderTextField
                                                        select
                                                        size="small"
                                                        value={trailer.dclocation || ''}
                                                        onChange={(e) =>
                                                            onDataChange(column.accessorKey, e.target.value, trailer.id)
                                                        }
                                                        fullWidth
                                                    >
                                                        {storeOptions.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </NoBorderTextField>
                                                </TableCell>
                                            );
                                        }

                                        if (column.accessorKey === 'products') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    {trailer?.products?.reduce(
                                                        (sum: number, item: any) => sum + (Number(item.skid) || 0),
                                                        0
                                                    )}
                                                </TableCell>
                                            );
                                        }

                                        if (column.accessorKey === 'received_date') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    <NoBorderTextField
                                                        size="small"
                                                        type="date"
                                                        value={trailer?.received_date?.split?.('T')?.[0] || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => onDateChange(e, trailer.id)}
                                                        fullWidth
                                                    />
                                                </TableCell>
                                            );
                                        }

                                        if (column.accessorKey === 'images') {
                                            return (
                                                <TableCell
                                                    key={column.accessorKey}
                                                    sx={{
                                                        width: 90,
                                                        minWidth: 90,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <FileView
                                                            id={trailer.id}
                                                            fetchApi="/uploads/trailers/"
                                                            deleteApi="/trailer/deleteImage"
                                                            files={trailer?.images || []}
                                                        />

                                                        <FileUpload
                                                            uploadApi="/trailer/uploadImage"
                                                            id={trailer.id}
                                                            trailer_no={trailer.trailer_no}
                                                            varient="unstyled"
                                                            setTrailers={setTrailers}
                                                        />
                                                    </Box>
                                                </TableCell>
                                            );
                                        }

                                        if (column.accessorKey === 'zone') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    <NoBorderTextField
                                                        select
                                                        size="small"
                                                        value={trailer?.info?.zone || ''}
                                                        onChange={(e) =>
                                                            onInfoChange('zone', e.target.value, trailer.id)
                                                        }
                                                        fullWidth
                                                    >
                                                        {ZONE_OPTIONS.map((option: any) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </NoBorderTextField>
                                                </TableCell>
                                            );
                                        }

                                        if (column.accessorKey === 'delete') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => onDeleteTrailer(trailer.trailer_no)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            );
                                        }
                                        if (column.accessorKey === 'qr') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    <QRCode trailer={trailer} />
                                                </TableCell>
                                            );
                                        }

                                        if (column.accessorKey === 'info') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <IconButton size="small" onClick={() => onMapOpen(trailer)}>
                                                            <PlaceIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onGetMap(trailer.info)}
                                                        >
                                                            <MapIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            );
                                        }

                                        if (column.accessorKey === 'cost') {
                                            return (
                                                <TableCell key={column.accessorKey}>
                                                    <NoBorderTextField
                                                        size="small"
                                                        type="number"
                                                        name="cost"
                                                        value={trailer[column.accessorKey] || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => onTextChange(e, trailer.id)}
                                                        fullWidth
                                                    />
                                                </TableCell>
                                            );
                                        }

                                        return (
                                            <TableCell key={column.accessorKey}>
                                                <NoBorderTextField
                                                    size="small"
                                                    name={column.accessorKey}
                                                    value={trailer[column.accessorKey] || ''}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) => onTextChange(e, trailer.id)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>

                                {isExpanded && (<TableRow>
                                    <TableCell colSpan={columns.length + 1} sx={{ p: 0, borderBottom: 0 }}>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <TrailerExpandedItems
                                                trailer={trailer}
                                                onAddRow={onAddRow}
                                                onDeleteRow={onDeleteRow}
                                                onProductChange={onProductChange}
                                                onDeleteTrailer={onDeleteTrailer}
                                            />
                                        </Collapse>
                                    </TableCell>
                                </TableRow>)}
                            </React.Fragment>
                        );
                    })}

                    {trailers.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={columns.length + 1}>
                                <Typography variant="body2" align="center" sx={{ py: 3 }}>
                                    No trailers found.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Paper>
    );
};

export default TrailerTable;